import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  User as UserIcon, Clock, Calendar, MapPin, KeyReturn, PlusCircle,
  Trash, PaperPlaneTilt, Coffee, SignIn, SignOut, ListChecks, Briefcase,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const RATE_DEFAULT = 0.26;
const TODAY = () => new Date().toISOString().slice(0, 10);
const MONTH_NOW = () => new Date().toISOString().slice(0, 7);

function fmtHours(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(11, 16);
  }
}

export default function MiPerfil() {
  const { user } = useAuth();

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="mi-perfil-page">
      <header className="flex items-center gap-3 mb-2">
        <UserIcon className="w-7 h-7 text-brand" weight="duotone" />
        <div>
          <h1 className="text-2xl font-semibold">Mi perfil</h1>
          <p className="text-sm text-muted">{user?.name} · <span className="font-mono">{user?.code}</span> · {user?.email}</p>
        </div>
      </header>

      <ProfileDataSection user={user} />
      <MicrosoftCalendarSection />
      <PasswordSection />
      <div className="bg-brand-soft border border-brand/30 rounded-[10px] px-4 py-3 text-[13px] text-foreground" data-testid="control-gestiones-cta">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold">Control horario, kilometraje, horario y vacaciones</div>
            <div className="help-text">Ahora viven en una página dedicada con exportación a PDF y Excel.</div>
          </div>
          <a href="/mi-control-gestiones" data-testid="open-control-gestiones"
             className="text-[12.5px] inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-brand text-white font-semibold hover:bg-brand/90 transition-colors">
            Abrir &quot;Mi control y gestiones&quot; →
          </a>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 1) DATOS DEL PERFIL
// ============================================================
function ProfileDataSection({ user }) {
  const [data, setData] = useState({ telefono: "", direccion_salida: "", movil_emergencia: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/me/profile").then((r) => {
      if (!alive) return;
      setData({
        telefono: r.data.telefono || "",
        direccion_salida: r.data.direccion_salida || "",
        movil_emergencia: r.data.movil_emergencia || "",
      });
    });
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/me/profile", data);
      toast.success("Datos actualizados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Mis datos" icon={<UserIcon className="w-5 h-5 text-brand" weight="duotone" />}>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <ReadOnlyField label="Nombre" value={user?.name} />
        <ReadOnlyField label="Email" value={user?.email} />
        <ReadOnlyField label="Código" value={user?.code} mono />
        <ReadOnlyField label="Rol" value={user?.role} />
      </div>
      <p className="text-xs text-muted mb-3">
        Para cambiar nombre, email, rol u otros datos fiscales (DNI, IBAN), pide al administrador.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Teléfono">
          <input
            type="tel"
            value={data.telefono}
            onChange={(e) => setData({ ...data, telefono: e.target.value })}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="profile-telefono"
          />
        </Field>
        <Field label="Móvil de emergencia">
          <input
            type="tel"
            value={data.movil_emergencia}
            onChange={(e) => setData({ ...data, movil_emergencia: e.target.value })}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="profile-movil-emergencia"
          />
        </Field>
        <Field label="Dirección de salida (para cálculo de km)" className="sm:col-span-2">
          <input
            type="text"
            placeholder="C/ Mayor 12, 37001 Salamanca"
            value={data.direccion_salida}
            onChange={(e) => setData({ ...data, direccion_salida: e.target.value })}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="profile-direccion-salida"
          />
        </Field>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={save}
          disabled={loading}
          data-testid="profile-save"
          className="bg-foreground text-white text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50"
        >
          {loading ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// 2) CAMBIAR CONTRASEÑA
// ============================================================
function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [nuevo, setNuevo] = useState("");
  const [loading, setLoading] = useState(false);

  const policyOk = useMemo(() => {
    if (nuevo.length < 10) return false;
    if (!/[A-ZÁÉÍÓÚÑ]/.test(nuevo)) return false;
    if (!/\d{2,}/.test(nuevo)) return false;
    if (!/\s/.test(nuevo)) return false;
    return true;
  }, [nuevo]);

  const submit = async () => {
    if (!current || !nuevo) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: nuevo });
      toast.success("Contraseña actualizada");
      setCurrent("");
      setNuevo("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cambiando contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Cambiar contraseña" icon={<KeyReturn className="w-5 h-5 text-brand" weight="duotone" />}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Contraseña actual">
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="pwd-current"
          />
        </Field>
        <Field label="Nueva contraseña">
          <input
            type="password"
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="pwd-new"
          />
        </Field>
      </div>
      <ul className="mt-3 text-xs space-y-1">
        <PolicyLine ok={nuevo.length >= 10} text="Mínimo 10 caracteres" />
        <PolicyLine ok={/[A-ZÁÉÍÓÚÑ]/.test(nuevo)} text="Al menos una mayúscula" />
        <PolicyLine ok={/\d{2,}/.test(nuevo)} text="Un número de 2+ cifras (ej: 40, 1980)" />
        <PolicyLine ok={/\s/.test(nuevo)} text="Debe ser una frase (con espacios) — ej: El sol de los 40" />
      </ul>
      <div className="mt-4 flex justify-end">
        <button
          onClick={submit}
          disabled={loading || !current || !policyOk}
          data-testid="pwd-submit"
          className="bg-foreground text-white text-sm px-4 py-2 rounded-md font-medium disabled:opacity-40"
        >
          {loading ? "Guardando…" : "Cambiar contraseña"}
        </button>
      </div>
    </Card>
  );
}

function PolicyLine({ ok, text }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-green-700" : "text-stone-500"}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-green-500" : "bg-stone-300"}`} />
      {text}
    </li>
  );
}

// ============================================================
// 3) CONTROL HORARIO (RD-Ley 8/2019)
// ============================================================
function JornadaSection() {
  const [today, setToday] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [month, setMonth] = useState(MONTH_NOW());

  const refresh = useCallback(async () => {
    const r = await api.get("/me/jornada/today");
    setToday(r.data);
    const m = await api.get(`/me/jornada/month?month=${month}`);
    setMonthData(m.data);
  }, [month]);

  useEffect(() => { refresh(); }, [refresh]);

  const clock = async (type) => {
    try {
      await api.post("/me/jornada/clock", { type });
      const labels = { clock_in: "Fichaje de entrada registrado", clock_out: "Fichaje de salida registrado", break_start: "Inicio de pausa registrado", break_end: "Fin de pausa registrado" };
      toast.success(labels[type] || "Fichaje registrado");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error registrando fichaje");
    }
  };

  const state = today?.state || "off";
  const stateLabel = state === "working" ? "🟢 Trabajando" : state === "on_break" ? "🟡 En pausa" : "⚪ Fuera de jornada";

  return (
    <Card title="Control horario (RD-Ley 8/2019)" icon={<Clock className="w-5 h-5 text-brand" weight="duotone" />}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-semibold">{stateLabel}</div>
          <div className="text-xs text-muted">Hoy llevas <b>{today ? fmtHours(today.worked_minutes) : "—"}</b> trabajados</div>
        </div>
        <div className="flex gap-2">
          {state === "off" && (
            <button onClick={() => clock("clock_in")} data-testid="clock-in" className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-2 rounded-md font-medium inline-flex items-center gap-1.5">
              <SignIn className="w-4 h-4" weight="bold" /> Entrada
            </button>
          )}
          {state === "working" && (
            <>
              <button onClick={() => clock("break_start")} data-testid="break-start" className="bg-amber-500 hover:bg-amber-600 text-white text-sm px-3 py-2 rounded-md font-medium inline-flex items-center gap-1.5">
                <Coffee className="w-4 h-4" weight="bold" /> Pausa
              </button>
              <button onClick={() => clock("clock_out")} data-testid="clock-out" className="bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-2 rounded-md font-medium inline-flex items-center gap-1.5">
                <SignOut className="w-4 h-4" weight="bold" /> Salida
              </button>
            </>
          )}
          {state === "on_break" && (
            <button onClick={() => clock("break_end")} data-testid="break-end" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-2 rounded-md font-medium inline-flex items-center gap-1.5">
              <SignIn className="w-4 h-4" weight="bold" /> Reanudar
            </button>
          )}
        </div>
      </div>

      {today?.events?.length > 0 && (
        <div className="mb-4 bg-stone-50 rounded-md p-3 border border-stone-200 text-xs">
          <div className="font-semibold mb-2">Fichajes de hoy</div>
          <div className="flex flex-wrap gap-2">
            {today.events.map((ev) => (
              <span key={ev.id} className="bg-white border border-stone-200 px-2 py-1 rounded font-mono">
                {fmtTime(ev.timestamp)} · {ev.type.replace("_", " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium">Resumen mensual</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-2 py-1 border rounded-md text-sm"
          data-testid="jornada-month"
        />
      </div>
      {monthData && (
        <div className="text-sm">
          <div className="mb-2">
            Total: <b>{fmtHours(monthData.total_minutes)}</b>
            {" "}({monthData.total_hours.toFixed(1)} h en {monthData.days?.length || 0} días)
          </div>
          {monthData.days?.length > 0 ? (
            <table className="w-full text-xs">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left py-1 px-2 font-medium">Día</th>
                  <th className="text-right py-1 px-2 font-medium">Horas</th>
                  <th className="text-left py-1 px-2 font-medium">Fichajes</th>
                </tr>
              </thead>
              <tbody>
                {monthData.days.map((d) => (
                  <tr key={d.date} className="border-t">
                    <td className="py-1 px-2 font-mono">{d.date}</td>
                    <td className="py-1 px-2 text-right font-semibold">{fmtHours(d.worked_minutes)}</td>
                    <td className="py-1 px-2 font-mono text-stone-600">
                      {d.events.map((e) => `${fmtTime(e.timestamp)} ${e.type.replace("_", " ")}`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-muted">Sin fichajes este mes.</p>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted mt-3">
        Los registros se conservan durante <b>4 años</b> conforme al RD-Ley 8/2019.
      </p>
    </Card>
  );
}

// ============================================================
// 4) KILOMETRAJE
// ============================================================
function KmExpensesSection() {
  const [items, setItems] = useState([]);
  const [totalKms, setTotalKms] = useState(0);
  const [totalEur, setTotalEur] = useState(0);
  const [month, setMonth] = useState(MONTH_NOW());
  const [draft, setDraft] = useState({ fecha: TODAY(), ruta: "", kms: "", tarifa: RATE_DEFAULT });

  const load = useCallback(async () => {
    const r = await api.get(`/me/km-expenses?month=${month}`);
    setItems(r.data.items || []);
    setTotalKms(r.data.total_kms || 0);
    setTotalEur(r.data.total_eur || 0);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.ruta || !draft.kms) {
      toast.error("Ruta y kilómetros son obligatorios");
      return;
    }
    try {
      await api.post("/me/km-expenses", {
        fecha: draft.fecha,
        ruta: draft.ruta.trim(),
        kms: parseFloat(draft.kms),
        tarifa_eur_km: parseFloat(draft.tarifa) || RATE_DEFAULT,
      });
      toast.success("Trayecto añadido");
      setDraft({ fecha: TODAY(), ruta: "", kms: "", tarifa: parseFloat(draft.tarifa) || RATE_DEFAULT });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar este trayecto?")) return;
    try {
      await api.delete(`/me/km-expenses/${id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const submit = async () => {
    const pending = items.filter((i) => i.status === "borrador");
    if (!pending.length) {
      toast.error("No hay trayectos en borrador para enviar");
      return;
    }
    if (!confirm(`Enviar ${pending.length} trayecto(s) de ${month} al Gestor Senior para minutar (${pending.reduce((s, i) => s + i.importe, 0).toFixed(2)} €)?`)) return;
    try {
      const r = await api.post("/me/km-expenses/submit", { month });
      toast.success(`Enviado a ${r.data.notified_gestores} gestor(es) · ${r.data.total_eur} €`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error enviando");
    }
  };

  const importePreview = (parseFloat(draft.kms) || 0) * (parseFloat(draft.tarifa) || RATE_DEFAULT);

  return (
    <Card title="Kilometraje del mes" icon={<MapPin className="w-5 h-5 text-brand" weight="duotone" />}>
      <div className="flex items-center justify-between mb-3">
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="px-2 py-1 border rounded-md text-sm"
          data-testid="km-month"
        />
        <div className="text-sm">
          Total: <b>{totalKms.toFixed(0)} km</b> · <b className="text-brand">{totalEur.toFixed(2)} €</b>
        </div>
      </div>

      {/* Form alta */}
      <div className="bg-stone-50 rounded-md p-3 mb-4 border border-stone-200">
        <div className="grid grid-cols-12 gap-2 items-end">
          <Field label="Fecha" className="col-span-3">
            <input type="date" value={draft.fecha} onChange={(e) => setDraft({ ...draft, fecha: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="km-fecha" />
          </Field>
          <Field label="Ruta" className="col-span-5">
            <input type="text" placeholder="Salamanca - Benavente - Salamanca" value={draft.ruta} onChange={(e) => setDraft({ ...draft, ruta: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="km-ruta" />
          </Field>
          <Field label="Km" className="col-span-2">
            <input type="number" placeholder="148" value={draft.kms} onChange={(e) => setDraft({ ...draft, kms: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm text-right" data-testid="km-kms" />
          </Field>
          <Field label="€/km" className="col-span-2">
            <input type="number" step="0.01" value={draft.tarifa} onChange={(e) => setDraft({ ...draft, tarifa: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm text-right" data-testid="km-tarifa" />
          </Field>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-muted">Importe del trayecto: <b className="text-brand">{importePreview.toFixed(2)} €</b></div>
          <button onClick={add} data-testid="km-add" className="bg-brand text-white text-sm px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" weight="bold" /> Añadir trayecto
          </button>
        </div>
      </div>

      {/* Listado */}
      {items.length === 0 ? (
        <p className="text-sm text-muted text-center py-4">Sin trayectos en {month}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs">
            <tr>
              <th className="text-left py-2 px-2 font-medium">Fecha</th>
              <th className="text-left py-2 px-2 font-medium">Ruta</th>
              <th className="text-right py-2 px-2 font-medium">Km</th>
              <th className="text-right py-2 px-2 font-medium">€</th>
              <th className="text-center py-2 px-2 font-medium">Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="py-1.5 px-2 font-mono text-xs">{i.fecha}</td>
                <td className="py-1.5 px-2">{i.ruta}</td>
                <td className="py-1.5 px-2 text-right">{i.kms.toFixed(0)}</td>
                <td className="py-1.5 px-2 text-right font-semibold">{i.importe.toFixed(2)} €</td>
                <td className="py-1.5 px-2 text-center"><KmStatusBadge status={i.status} /></td>
                <td className="py-1.5 px-2 text-right">
                  {i.status === "borrador" && (
                    <button onClick={() => del(i.id)} className="text-red-600 hover:text-red-800" title="Borrar" data-testid={`km-del-${i.id}`}>
                      <Trash className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-4 flex justify-end">
        <button
          onClick={submit}
          disabled={items.filter((i) => i.status === "borrador").length === 0}
          data-testid="km-submit"
          className="bg-foreground text-white text-sm px-4 py-2 rounded-md font-medium inline-flex items-center gap-2 disabled:opacity-40"
        >
          <PaperPlaneTilt className="w-4 h-4" weight="bold" />
          Enviar a Gestor Senior para minutar
        </button>
      </div>
    </Card>
  );
}

function KmStatusBadge({ status }) {
  const map = {
    borrador: { bg: "bg-stone-100 text-stone-700", label: "Borrador" },
    enviado: { bg: "bg-blue-100 text-blue-800", label: "Enviado" },
    aprobado: { bg: "bg-green-100 text-green-800", label: "Aprobado" },
    liquidado: { bg: "bg-emerald-200 text-emerald-900", label: "Liquidado" },
    rechazado: { bg: "bg-red-100 text-red-800", label: "Rechazado" },
  };
  const s = map[status] || map.borrador;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.bg}`}>{s.label}</span>;
}

// ============================================================
// 5) HORARIOS LABORABLES
// ============================================================
function ScheduleSection({ user }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/calendar/schedule/${user.id}`).then((r) => setSchedule(r.data));
  }, [user?.id]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/calendar/schedule/${user.id}`, schedule);
      toast.success("Horario guardado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (!schedule) return null;

  const days = [
    { key: "lunes", label: "Lunes" },
    { key: "martes", label: "Martes" },
    { key: "miercoles", label: "Miércoles" },
    { key: "jueves", label: "Jueves" },
    { key: "viernes", label: "Viernes" },
    { key: "sabado", label: "Sábado" },
    { key: "domingo", label: "Domingo" },
  ];

  return (
    <Card title="Mis horarios laborables" icon={<Briefcase className="w-5 h-5 text-brand" weight="duotone" />}>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-xs">
          <tr>
            <th className="text-left py-2 px-2 font-medium">Día</th>
            <th className="text-center py-2 px-2 font-medium">Trabajo</th>
            <th className="text-center py-2 px-2 font-medium">Inicio</th>
            <th className="text-center py-2 px-2 font-medium">Fin</th>
            <th className="text-center py-2 px-2 font-medium">Pausa inicio</th>
            <th className="text-center py-2 px-2 font-medium">Pausa fin</th>
          </tr>
        </thead>
        <tbody>
          {days.map(({ key, label }) => {
            const d = schedule[key] || {};
            const upd = (patch) => setSchedule({ ...schedule, [key]: { ...d, ...patch } });
            return (
              <tr key={key} className="border-t">
                <td className="py-1.5 px-2 font-medium">{label}</td>
                <td className="py-1.5 px-2 text-center">
                  <input type="checkbox" checked={d.enabled || false} onChange={(e) => upd({ enabled: e.target.checked })} data-testid={`sched-${key}-enabled`} />
                </td>
                <td className="py-1.5 px-2 text-center"><input type="time" value={d.morning_start || ""} onChange={(e) => upd({ morning_start: e.target.value })} disabled={!d.enabled} className="px-1 py-0.5 border rounded text-xs disabled:opacity-30" /></td>
                <td className="py-1.5 px-2 text-center"><input type="time" value={d.afternoon_end || ""} onChange={(e) => upd({ afternoon_end: e.target.value })} disabled={!d.enabled} className="px-1 py-0.5 border rounded text-xs disabled:opacity-30" /></td>
                <td className="py-1.5 px-2 text-center"><input type="time" value={d.morning_end || ""} onChange={(e) => upd({ morning_end: e.target.value })} disabled={!d.enabled} className="px-1 py-0.5 border rounded text-xs disabled:opacity-30" /></td>
                <td className="py-1.5 px-2 text-center"><input type="time" value={d.afternoon_start || ""} onChange={(e) => upd({ afternoon_start: e.target.value })} disabled={!d.enabled} className="px-1 py-0.5 border rounded text-xs disabled:opacity-30" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={loading} data-testid="sched-save" className="bg-foreground text-white text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50">
          {loading ? "Guardando…" : "Guardar horario"}
        </button>
      </div>
    </Card>
  );
}

// ============================================================
// 6) VACACIONES
// ============================================================
function VacationsSection({ user }) {
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState({ start_date: "", end_date: "", concept: "Vacaciones" });

  const load = useCallback(async () => {
    if (!user?.id) return;
    const r = await api.get(`/calendar/schedule/${user.id}`);
    setList(r.data?.vacations || []);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const request = async () => {
    if (!draft.start_date || !draft.end_date) {
      toast.error("Fechas obligatorias");
      return;
    }
    try {
      await api.post(`/calendar/schedule/${user.id}/vacations`, draft);
      toast.success("Solicitud enviada");
      setDraft({ start_date: "", end_date: "", concept: "Vacaciones" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <Card title="Vacaciones y ausencias" icon={<Calendar className="w-5 h-5 text-brand" weight="duotone" />}>
      <div className="bg-stone-50 rounded-md p-3 mb-4 border border-stone-200">
        <div className="grid grid-cols-12 gap-2 items-end">
          <Field label="Desde" className="col-span-3"><input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="vac-start" /></Field>
          <Field label="Hasta" className="col-span-3"><input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="vac-end" /></Field>
          <Field label="Concepto" className="col-span-4"><input type="text" value={draft.concept} onChange={(e) => setDraft({ ...draft, concept: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="vac-concept" /></Field>
          <div className="col-span-2"><button onClick={request} data-testid="vac-request" className="w-full bg-brand text-white text-sm px-3 py-2 rounded-md font-medium inline-flex items-center justify-center gap-1.5"><ListChecks className="w-4 h-4" weight="bold" /> Solicitar</button></div>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-muted text-center py-4">No tienes solicitudes registradas</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs">
            <tr>
              <th className="text-left py-2 px-2 font-medium">Desde</th>
              <th className="text-left py-2 px-2 font-medium">Hasta</th>
              <th className="text-left py-2 px-2 font-medium">Concepto</th>
              <th className="text-center py-2 px-2 font-medium">Estado</th>
            </tr>
          </thead>
          <tbody>
            {list.map((v) => (
              <tr key={v.id} className="border-t">
                <td className="py-1.5 px-2 font-mono text-xs">{v.start_date}</td>
                <td className="py-1.5 px-2 font-mono text-xs">{v.end_date}</td>
                <td className="py-1.5 px-2">{v.concept}</td>
                <td className="py-1.5 px-2 text-center"><VacStatusBadge status={v.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function VacStatusBadge({ status }) {
  const map = {
    requested: { bg: "bg-blue-100 text-blue-800", label: "Solicitado" },
    approved: { bg: "bg-green-100 text-green-800", label: "Aprobado" },
    rejected: { bg: "bg-red-100 text-red-800", label: "Rechazado" },
  };
  const s = map[status] || map.requested;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.bg}`}>{s.label}</span>;
}

// ============================================================
// HELPERS
// ============================================================
function Card({ title, icon, children }) {
  return (
    <section className="bg-white rounded-lg border border-stone-200 shadow-sm">
      <header className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
        {icon}
        <h2 className="text-base font-semibold">{title}</h2>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-stone-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value, mono = false }) {
  return (
    <div>
      <label className="block text-xs font-medium text-stone-600 mb-1">{label}</label>
      <div className={`px-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-sm text-stone-700 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}


// ============================================================
// MICROSOFT CALENDAR — Conectar la cuenta personal del perito
// ============================================================
function MicrosoftCalendarSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/ms-calendar/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Si volvemos del callback ?connected=outlook, recargamos.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/integrations/ms-calendar/connect");
      const w = window.open(r.data.url, "ms_oauth", "width=520,height=720");
      // Polling para detectar cuando el popup termine
      const iv = setInterval(() => {
        if (!w || w.closed) {
          clearInterval(iv);
          load();
        }
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando OAuth");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar tu cuenta Microsoft? Las citas existentes en Outlook no se borrarán.")) return;
    setBusy(true);
    try {
      await api.delete("/integrations/ms-calendar/disconnect");
      toast.success("Cuenta desconectada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al desconectar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Sincronizar con Outlook (Microsoft 365)" icon={
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#0078D4]" fill="currentColor">
        <rect x="2" y="2" width="9" height="9"/><rect x="13" y="2" width="9" height="9"/>
        <rect x="2" y="13" width="9" height="9"/><rect x="13" y="13" width="9" height="9"/>
      </svg>
    }>
      <p className="text-[13px] text-muted mb-4">
        Conecta tu cuenta <strong>corporativa de Microsoft 365</strong> (la del dominio
        de tu empresa, p.ej. <code className="text-[12px]">tunombre@dobilas.es</code>).
        Las citas creadas en tu agenda de Ekspertium aparecerán automáticamente en tu
        Outlook (móvil y escritorio), y si reagendas o cancelas en Outlook también se
        actualizará aquí.
      </p>
      {loading ? (
        <div className="text-sm text-muted">Comprobando estado…</div>
      ) : status?.connected ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="ms-calendar-connected">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] font-semibold">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            Conectado · {status.email}
          </span>
          <button
            onClick={disconnect}
            disabled={busy}
            data-testid="ms-calendar-disconnect-btn"
            className="text-[13px] px-3 py-1.5 border border-red-300 text-red-700 rounded-md hover:bg-red-50 transition disabled:opacity-50"
          >
            {busy ? "Desconectando…" : "Desconectar"}
          </button>
          {status.subscription_expires_at && (
            <span className="text-[11px] text-stone-400 ml-auto">
              Renovación próxima: {new Date(status.subscription_expires_at).toLocaleString("es-ES")}
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3" data-testid="ms-calendar-disconnected">
          {status?.status === "revoked" && (
            <span className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
              Acceso revocado · vuelve a conectar para reactivar la sincronización
            </span>
          )}
          <button
            onClick={connect}
            disabled={busy}
            data-testid="ms-calendar-connect-btn"
            className="inline-flex items-center gap-2 bg-[#0078D4] hover:bg-[#106EBE] text-white text-sm font-semibold px-4 py-2 rounded-md transition disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <rect x="2" y="2" width="9" height="9"/><rect x="13" y="2" width="9" height="9"/>
              <rect x="2" y="13" width="9" height="9"/><rect x="13" y="13" width="9" height="9"/>
            </svg>
            {busy ? "Abriendo Microsoft…" : "Conectar con Microsoft"}
          </button>
        </div>
      )}
      <details className="mt-4 text-xs text-stone-500">
        <summary className="cursor-pointer hover:text-stone-700">¿Qué permisos pide?</summary>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><code>Calendars.ReadWrite</code> · leer y escribir solo en TU calendario.</li>
          <li>No accedemos a tus correos, contactos ni otros datos.</li>
          <li>Puedes desconectar en cualquier momento desde aquí.</li>
        </ul>
      </details>
    </Card>
  );
}
